import React, { useState, useEffect } from 'react';
import { Shield, LogOut, Download, Trash2, Eye, AlertTriangle, Target, Settings, Github, Bell, BarChart3, BookOpen, User, Lock, Mail, X, Building2, Terminal, Cloud } from 'lucide-react';
import MultiFormatInput from '../components/MultiFormatInput';
import GitHubScanner from '../components/GitHubScanner';
import AzureDevOpsScanner from '../components/AzureDevOpsScanner';
import NotificationCenter from '../components/NotificationCenter';
import ScanResultsDashboard from '../components/ScanResultsDashboard';
import Documentation from '../components/Documentation';
import UserProfile from '../components/UserProfile';
import CICDReports from '../components/CICDReports';
import { AdminDashboard } from '../components/admin';
import OrganizationSettings from '../components/OrganizationSettings';
import ThreatModelerAPI from '../services/api';

const ThreatModeler = ({ keycloak, onLogout, user }) => {
  const [api] = useState(() => new ThreatModelerAPI(keycloak));

  // License info from backend
  const [licenseInfo, setLicenseInfo] = useState(null);
  const [licenseLoading, setLicenseLoading] = useState(true);

  // Determine if user has unlimited access based on license info
  const isUnlimitedUser = licenseInfo?.is_admin === true ||
                          licenseInfo?.license_type === 'enterprise' ||
                          licenseInfo?.scan_limit === null;

  // Get scan limits from license info
  const scanLimit = licenseInfo?.scan_limit || 1;
  const scansUsed = licenseInfo?.scans_used || 0;
  const scansRemaining = licenseInfo?.scans_remaining;
  const [activeMainTab, setActiveMainTab] = useState('threat-modeling');
  const [jobs, setJobs] = useState([]);
  const [currentJob, setCurrentJob] = useState(null);
  const [loading, setLoading] = useState(false);
  const [systemType, setSystemType] = useState('web_app');
  const [role, setRole] = useState('baseline');
  const [enableRiskScoring, setEnableRiskScoring] = useState(false);
  const [enableVisualization, setEnableVisualization] = useState(false);
  const [exportFormats, setExportFormats] = useState([]);
  const [llmProvider, setLlmProvider] = useState('openai');
  const [selectedFrameworks, setSelectedFrameworks] = useState(['owasp', 'stride', 'mitre', 'iso42001']);
  const [showLimitModal, setShowLimitModal] = useState(false);
  const [limitType, setLimitType] = useState('');

  // Load license info on mount
  useEffect(() => {
    loadLicenseInfo();
  }, []);

  const loadLicenseInfo = async () => {
    try {
      setLicenseLoading(true);
      const info = await api.getUserLicense();
      setLicenseInfo(info);
    } catch (error) {
      console.error('Failed to load license info:', error);
      // Default to basic free tier if license info fails to load
      setLicenseInfo({
        is_admin: false,
        license_type: 'free',
        scan_limit: 5,
        scans_used: 0,
        scans_remaining: 5
      });
    } finally {
      setLicenseLoading(false);
    }
  };

  // Poll for job updates
  useEffect(() => {
    if (currentJob && (currentJob.status === 'pending' || currentJob.status === 'running')) {
      const interval = setInterval(async () => {
        try {
          const updated = await api.getJobStatus(currentJob.job_id);
          setCurrentJob(updated);

          if (updated.status === 'completed' || updated.status === 'failed') {
            clearInterval(interval);
            loadJobs(); // Refresh job list
          }
        } catch (error) {
          console.error('Error polling job status:', error);
        }
      }, 2000);

      return () => clearInterval(interval);
    }
  }, [currentJob, api]);


  // Load jobs on mount
  useEffect(() => {
    loadJobs();
  }, []);

  const loadJobs = async () => {
    try {
      // Exclude API (CI/CD) and GitHub scanner jobs - only show UI-originated jobs
      const response = await api.listJobs({
        exclude_sources: 'api,github_scanner'
      });
      setJobs(response.jobs || []);
    } catch (error) {
      console.error('Error loading jobs:', error);
    }
  };

  const checkUsageLimit = (type) => {
    // Unlimited users bypass all limits
    if (isUnlimitedUser) {
      return true;
    }
    // Check scans remaining from backend
    if (scansRemaining !== null && scansRemaining <= 0) {
      setLimitType(type === 'threatModel' ? 'threat model scan' : 'GitHub organization scan');
      setShowLimitModal(true);
      return false;
    }
    return true;
  };

  const refreshLicenseInfo = async () => {
    // Reload license info after a scan to update counts
    await loadLicenseInfo();
  };

  const handleSubmit = async (formData) => {
    // Check usage limit before proceeding
    if (!checkUsageLimit('threatModel')) {
      return;
    }

    setLoading(true);
    try {
      // Add configuration to formData
      if (formData instanceof FormData) {
        formData.append('system_type', systemType);
        formData.append('role', role);
        formData.append('enable_risk_scoring', enableRiskScoring);
        formData.append('visualize', enableVisualization);
        formData.append('export_formats', JSON.stringify(exportFormats));
        formData.append('llm_provider', llmProvider);
        formData.append('selected_frameworks', JSON.stringify(selectedFrameworks));
      } else {
        // JSON payload
        formData.system_type = systemType;
        formData.role = role;
        formData.enable_risk_scoring = enableRiskScoring;
        formData.visualize = enableVisualization;
        formData.export_formats = exportFormats;
        formData.llm_provider = llmProvider;
        formData.selected_frameworks = selectedFrameworks;
      }

      const response = await api.analyzeSystem(formData);
      const jobStatus = await api.getJobStatus(response.job_id);
      setCurrentJob(jobStatus);

      // Refresh license info after successful submission to update counts
      await refreshLicenseInfo();
    } catch (error) {
      alert(`Error: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Handler for GitHub org scan limit
  const handleGithubOrgScanAttempt = () => {
    return checkUsageLimit('githubOrg');
  };

  const handleGithubOrgScanSuccess = async () => {
    await refreshLicenseInfo();
  };


  const handleDownload = async (jobId, fileType) => {
    try {
      const data = await api.downloadFile(jobId, fileType);

      // Handle different file types
      if (fileType === 'json') {
        // For JSON, the data should be the full results object
        const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
        downloadBlob(blob, `threats_${jobId}.json`);
      } else if (fileType === 'markdown') {
        // For markdown, extract the report content
        const reportContent = typeof data === 'object' ? data.report : data;
        const blob = new Blob([reportContent], { type: 'text/markdown' });
        downloadBlob(blob, `report_${jobId}.md`);
      } else if (fileType === 'sarif' || fileType === 'spdx' || fileType === 'cyclonedx') {
        // For export formats, the data should already be the formatted content
        const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
        downloadBlob(blob, `${fileType}_${jobId}.json`);
      } else if (fileType === 'attack_graph' || fileType === 'risk_heatmap') {
        // For visualizations, data should be a blob
        if (data instanceof Blob) {
          downloadBlob(data, `${fileType}_${jobId}.png`);
        } else {
          throw new Error(`Visualization not available for ${fileType}`);
        }
      }
    } catch (error) {
      console.error('Download error:', error);
      alert(`Download error: ${error.message}`);
    }
  };

  const downloadBlob = (blob, filename) => {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const handleDelete = async (jobId) => {
    if (window.confirm('Are you sure you want to delete this analysis?')) {
      try {
        await api.deleteJob(jobId);
        setJobs(jobs.filter(j => j.job_id !== jobId));
        if (currentJob?.job_id === jobId) {
          setCurrentJob(null);
        }
      } catch (error) {
        alert(`Delete error: ${error.message}`);
      }
    }
  };

  const toggleExportFormat = (format) => {
    setExportFormats(prev =>
      prev.includes(format) ? prev.filter(f => f !== format) : [...prev, format]
    );
  };

  const toggleFramework = (framework) => {
    setSelectedFrameworks(prev =>
      prev.includes(framework) ? prev.filter(f => f !== framework) : [...prev, framework]
    );
  };

  const handleNavigateToProfile = (subTab = 'profile') => {
    setActiveMainTab('profile');
  };

  // Get license display info
  const getLicenseBadge = () => {
    if (licenseInfo?.is_admin) {
      return {
        label: 'Unlimited Access',
        style: { background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }
      };
    }
    switch (licenseInfo?.license_type) {
      case 'enterprise':
        return {
          label: 'Enterprise',
          style: { background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }
        };
      case 'professional':
        return {
          label: 'Professional',
          style: { background: 'linear-gradient(135deg, #4299e1 0%, #3182ce 100%)', color: 'white' }
        };
      default:
        return {
          label: `Free Tier: ${scansUsed}/${scanLimit} scans`,
          style: { background: '#f7fafc', color: '#4a5568' }
        };
    }
  };

  const licenseBadge = getLicenseBadge();

  // Usage Limit Modal Component
  const UsageLimitModal = () => (
    <div className="limit-modal-overlay" onClick={() => setShowLimitModal(false)}>
      <div className="limit-modal" onClick={e => e.stopPropagation()}>
        <div className="limit-modal-icon">
          <Lock size={32} />
        </div>
        <h2>Scan Limit Reached</h2>
        <p>
          You've used all {scanLimit} scans in your {licenseInfo?.license_type || 'free'} plan.
        </p>
        <p>
          To get more scans and unlock premium features, please upgrade your plan.
        </p>
        <div className="limit-modal-actions">
          <a
            href={`mailto:hello@agenticthreat.ai?subject=Upgrade Request&body=Hi, I've reached my scan limit and would like to upgrade my plan.%0A%0AEmail: ${user?.email}%0A%0ACurrent Plan: ${licenseInfo?.license_type || 'free'}%0ATenant: ${licenseInfo?.tenant_name || 'N/A'}%0A%0APlease send me more information about pricing and features.`}
            className="cta-primary"
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', textDecoration: 'none' }}
          >
            <Mail size={20} />
            Contact Sales
          </a>
          <button
            className="cta-secondary"
            onClick={() => setShowLimitModal(false)}
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}
          >
            <X size={20} />
            Close
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="threat-modeler-page">
      {/* Usage Limit Modal */}
      {showLimitModal && <UsageLimitModal />}

      {/* Header */}
      <header className="page-header">
        <div className="header-left">
          <Shield size={32} className="logo-icon" />
          <h1>Agentic Threat Modeler</h1>
        </div>
        <div className="header-right">
          <div className="usage-indicator" style={{
            ...licenseBadge.style,
            padding: '0.5rem 1rem',
            borderRadius: '8px',
            marginRight: '1rem',
            fontSize: '0.85rem',
            fontWeight: '600'
          }}>
            <span>{licenseBadge.label}</span>
          </div>
          <div className="user-info">
            <span className="user-name">{user.name}</span>
            <span className="user-email">{user.email}</span>
          </div>
          <button
            className="profile-btn"
            onClick={() => handleNavigateToProfile('profile')}
            title="User Profile"
          >
            <User size={20} />
            Profile
          </button>
          <button className="logout-btn" onClick={onLogout}>
            <LogOut size={20} />
            Logout
          </button>
        </div>
      </header>

      {/* Main Navigation Tabs */}
      <div className="main-navigation">
        <div className="nav-tabs">
          <button
            className={`nav-tab ${activeMainTab === 'threat-modeling' ? 'active' : ''}`}
            onClick={() => setActiveMainTab('threat-modeling')}
          >
            <Target size={20} />
            <span>Threat Modeling</span>
          </button>
          <button
            className={`nav-tab ${activeMainTab === 'github-scanning' ? 'active' : ''}`}
            onClick={() => setActiveMainTab('github-scanning')}
          >
            <Github size={20} />
            <span>GitHub Scanner</span>
          </button>
          <button
            className={`nav-tab ${activeMainTab === 'azure-devops-scanning' ? 'active' : ''}`}
            onClick={() => setActiveMainTab('azure-devops-scanning')}
          >
            <Cloud size={20} />
            <span>Azure DevOps Scanner</span>
          </button>
          <button
            className={`nav-tab ${activeMainTab === 'notifications' ? 'active' : ''}`}
            onClick={() => setActiveMainTab('notifications')}
          >
            <Bell size={20} />
            <span>Notifications</span>
          </button>
          <button
            className={`nav-tab ${activeMainTab === 'dashboard' ? 'active' : ''}`}
            onClick={() => setActiveMainTab('dashboard')}
          >
            <BarChart3 size={20} />
            <span>Dashboard</span>
          </button>
          <button
            className={`nav-tab ${activeMainTab === 'cicd-reports' ? 'active' : ''}`}
            onClick={() => setActiveMainTab('cicd-reports')}
          >
            <Terminal size={20} />
            <span>CI/CD Reports</span>
          </button>
          <button
            className={`nav-tab ${activeMainTab === 'documentation' ? 'active' : ''}`}
            onClick={() => setActiveMainTab('documentation')}
          >
            <BookOpen size={20} />
            <span>Documentation</span>
          </button>
          {licenseInfo?.is_tenant_admin && !licenseInfo?.is_super_admin && (
            <button
              className={`nav-tab ${activeMainTab === 'org-settings' ? 'active' : ''}`}
              onClick={() => setActiveMainTab('org-settings')}
            >
              <Settings size={20} />
              <span>Organization</span>
            </button>
          )}
          {licenseInfo?.is_super_admin && (
            <button
              className={`nav-tab ${activeMainTab === 'admin' ? 'active' : ''}`}
              onClick={() => setActiveMainTab('admin')}
            >
              <Building2 size={20} />
              <span>Admin</span>
            </button>
          )}
        </div>
      </div>

      <div className="page-content">
        {/* Threat Modeling Tab Content */}
        {activeMainTab === 'threat-modeling' && (
          <div className="threat-modeling-scanner">
            {/* Header */}
            <div className="threat-modeling-header">
              <div className="threat-modeling-header-left">
                <Target size={32} color="#16a085" />
                <div>
                  <h1>AI Threat Modeling</h1>
                  <p>Automated Security Threat Analysis using Multiple Frameworks</p>
                </div>
              </div>
              {!isUnlimitedUser && scansRemaining !== null && scansRemaining <= 0 && (
                <div style={{
                  background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                  color: 'white',
                  padding: '0.75rem 1.5rem',
                  borderRadius: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.75rem'
                }}>
                  <Lock size={20} />
                  <span>Scan limit reached</span>
                  <a
                    href="mailto:hello@agenticthreat.ai?subject=Upgrade Request"
                    style={{ color: 'white', fontWeight: '600' }}
                  >
                    Upgrade
                  </a>
                </div>
              )}
            </div>

            {/* Content */}
            <div className="threat-modeling-content">
              {/* Configuration Section */}
              <div className="threat-modeling-section">
                <div className="threat-modeling-section-header">
                  <h2>
                    <Settings size={24} />
                    Analysis Configuration
                  </h2>
                  <p>Configure your threat modeling parameters and analysis settings before providing system input.</p>
                </div>

                <div className="threat-modeling-config">
                  <div className="threat-modeling-config-grid">
                    <div className="threat-modeling-field">
                      <label className="threat-modeling-label">System Type</label>
                      <select
                        className="threat-modeling-select"
                        value={systemType}
                        onChange={e => setSystemType(e.target.value)}
                      >
                        <option value="general">General System</option>
                        <option value="web_app">Web Application</option>
                        <option value="agentic_ai">AI/ML System</option>
                        <option value="mcp_system">MCP System</option>
                        <option value="api">API Service</option>
                      </select>
                    </div>

                    <div className="threat-modeling-field">
                      <label className="threat-modeling-label">Analysis Role</label>
                      <select
                        className="threat-modeling-select"
                        value={role}
                        onChange={e => setRole(e.target.value)}
                      >
                        <option value="baseline">Baseline Security</option>
                        <option value="cloud_security">Cloud Security</option>
                        <option value="network_security">Network Security</option>
                        <option value="ai_security">AI/ML Security</option>
                      </select>
                    </div>

                    <div className="threat-modeling-field">
                      <label className="threat-modeling-label">LLM Provider</label>
                      <select
                        className="threat-modeling-select"
                        value={llmProvider}
                        onChange={e => setLlmProvider(e.target.value)}
                      >
                        <option value="openai">OpenAI (GPT-4o)</option>
                        <option value="anthropic">Anthropic (Claude Sonnet 4)</option>
                        <option value="mock">Mock (Testing)</option>
                      </select>
                    </div>
                  </div>

                  <div className="threat-modeling-frameworks">
                    <label className="threat-modeling-label">Security Frameworks:</label>
                    <div className="threat-modeling-framework-grid">
                      {[
                        { id: 'owasp', name: 'OWASP Top 10', description: 'Web application security risks' },
                        { id: 'owasp_llm', name: 'OWASP LLM Top 10', description: 'LLM-specific vulnerabilities' },
                        { id: 'owasp_agentic', name: 'OWASP Agentic (T1-T15)', description: 'Agentic AI threat modeling' },
                        { id: 'owasp_mcp', name: 'OWASP MCP Top 10', description: 'Model Context Protocol threats' },
                        { id: 'stride', name: 'STRIDE', description: 'Microsoft threat modeling' },
                        { id: 'mitre', name: 'MITRE ATT&CK', description: 'Adversarial tactics and techniques' },
                        { id: 'maestro', name: 'MAESTRO', description: '7-layer AI security framework' },
                        { id: 'linddun', name: 'LINDDUN', description: 'Privacy threat modeling' },
                        { id: 'iso42001', name: 'ISO 42001', description: 'AI management system compliance' }
                      ].map(framework => (
                        <label key={framework.id} className="threat-modeling-framework-checkbox">
                          <input
                            type="checkbox"
                            checked={selectedFrameworks.includes(framework.id)}
                            onChange={() => toggleFramework(framework.id)}
                          />
                          <span className="checkmark"></span>
                          <div className="framework-info">
                            <span className="framework-name">{framework.name}</span>
                            <span className="framework-description">{framework.description}</span>
                          </div>
                        </label>
                      ))}
                    </div>
                  </div>

                  <div className="threat-modeling-options">
                    <label className="threat-modeling-checkbox">
                      <input
                        type="checkbox"
                        checked={enableRiskScoring}
                        onChange={e => setEnableRiskScoring(e.target.checked)}
                      />
                      <span className="checkmark"></span>
                      Enable Risk Scoring (CVSS & DREAD)
                    </label>

                    <label className="threat-modeling-checkbox">
                      <input
                        type="checkbox"
                        checked={enableVisualization}
                        onChange={e => setEnableVisualization(e.target.checked)}
                      />
                      <span className="checkmark"></span>
                      Generate Attack Graphs & Visualizations
                    </label>
                  </div>

                  <div className="threat-modeling-export-formats">
                    <label className="threat-modeling-label">Export Formats:</label>
                    <div className="threat-modeling-format-grid">
                      {['sarif', 'spdx', 'cyclonedx'].map(format => (
                        <label key={format} className="threat-modeling-checkbox">
                          <input
                            type="checkbox"
                            checked={exportFormats.includes(format)}
                            onChange={() => toggleExportFormat(format)}
                          />
                          <span className="checkmark"></span>
                          {format.toUpperCase()}
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Input Section */}
              <div className="threat-modeling-section">
                <div className="threat-modeling-section-header">
                  <h2>System Input</h2>
                  <p>Provide your system architecture using any format: text description, diagrams, IaC templates, or structured documents.</p>
                </div>

                <div className="threat-modeling-input-area">
                  <MultiFormatInput
                    onSubmit={handleSubmit}
                    loading={loading}
                    disabled={!isUnlimitedUser && scansRemaining !== null && scansRemaining <= 0}
                  />
                  {!isUnlimitedUser && scansRemaining !== null && scansRemaining <= 0 && (
                    <div style={{
                      marginTop: '1rem',
                      padding: '1rem',
                      background: '#fff5f5',
                      border: '1px solid #fed7d7',
                      borderRadius: '8px',
                      textAlign: 'center'
                    }}>
                      <p style={{ color: '#c53030', margin: 0 }}>
                        You've used all your scans. <a href="mailto:hello@agenticthreat.ai?subject=Upgrade Request" style={{ color: '#667eea', fontWeight: '600' }}>Contact us</a> to upgrade for more scans.
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Results Section */}
              {currentJob && (
                <div className="threat-modeling-section">
                  <div className="threat-modeling-section-header">
                    <h2>Analysis Results</h2>
                    {currentJob.job_id && (
                      <button
                        className="threat-modeling-action-btn"
                        onClick={() => setCurrentJob(null)}
                        style={{ marginLeft: 'auto' }}
                      >
                        <X size={16} />
                        Close
                      </button>
                    )}
                  </div>

                  {/* Status */}
                  <div className={`threat-modeling-status threat-modeling-status-${currentJob.status}`}>
                    <div className="threat-modeling-status-indicator"></div>
                    <div className="threat-modeling-status-content">
                      <div className="threat-modeling-status-title">
                        Status: {currentJob.status.toUpperCase()}
                      </div>
                      {currentJob.progress && (
                        <div className="threat-modeling-status-progress">{currentJob.progress}</div>
                      )}
                    </div>
                  </div>

                  {/* Results */}
                  {currentJob.status === 'completed' && currentJob.results && (
                    <div className="threat-modeling-results">
                      <div className="threat-modeling-summary">
                        <div className="threat-modeling-summary-card">
                          <div className="threat-modeling-summary-icon">
                            <AlertTriangle size={24} color="#e74c3c" />
                          </div>
                          <div className="threat-modeling-summary-content">
                            <div className="threat-modeling-summary-number">
                              {currentJob.results.summary?.total_threats || currentJob.results.threat_model?.length || currentJob.threat_count || 0}
                            </div>
                            <div className="threat-modeling-summary-label">Threats Identified</div>
                          </div>
                        </div>
                        <div className="threat-modeling-summary-card">
                          <div className="threat-modeling-summary-icon">
                            <Shield size={24} color="#16a085" />
                          </div>
                          <div className="threat-modeling-summary-content">
                            <div className="threat-modeling-summary-number">
                              {currentJob.results.summary?.frameworks_applied?.length || currentJob.results.metadata?.frameworks?.length || 0}
                            </div>
                            <div className="threat-modeling-summary-label">Frameworks Applied</div>
                          </div>
                        </div>
                      </div>

                      {/* Threat List */}
                      {(currentJob.results.threat_model?.length > 0 || currentJob.results.threats?.length > 0) && (
                        <div className="threat-modeling-threats">
                          <h3>Identified Threats</h3>
                          <div className="threat-modeling-threat-list">
                            {(currentJob.results.threat_model || currentJob.results.threats || []).slice(0, 10).map((threat, index) => (
                              <div key={index} className="threat-modeling-threat-card">
                                <div className="threat-modeling-threat-header">
                                  <h4>Threat #{threat.id || index + 1}: {(threat.threat_scenario || threat.description || 'Unknown threat').substring(0, 150)}{(threat.threat_scenario || threat.description || '').length > 150 ? '...' : ''}</h4>
                                </div>
                                <div className="threat-modeling-threat-content">
                                  <div className="threat-modeling-threat-frameworks">
                                    {threat.owasp_categories?.length > 0 && (
                                      <span className="threat-modeling-framework-badge threat-modeling-owasp">
                                        OWASP: {threat.owasp_categories.join(', ')}
                                      </span>
                                    )}
                                    {threat.owasp_mcp_categories?.length > 0 && (
                                      <span className="threat-modeling-framework-badge threat-modeling-owasp-mcp">
                                        OWASP MCP: {threat.owasp_mcp_categories.join(', ')}
                                      </span>
                                    )}
                                    {threat.stride_categories?.length > 0 && (
                                      <span className="threat-modeling-framework-badge threat-modeling-stride">
                                        STRIDE: {threat.stride_categories.join(', ')}
                                      </span>
                                    )}
                                    {threat.mitre_tactics?.length > 0 && (
                                      <span className="threat-modeling-framework-badge threat-modeling-mitre">
                                        MITRE: {threat.mitre_tactics.join(', ')}
                                      </span>
                                    )}
                                    {threat.iso42001_categories?.length > 0 && (
                                      <span className="threat-modeling-framework-badge threat-modeling-iso42001">
                                        ISO 42001: {threat.iso42001_categories.join(', ')}
                                      </span>
                                    )}
                                  </div>
                                  {threat.cvss && (
                                    <div className={`threat-modeling-risk-score threat-modeling-severity-${(threat.cvss.severity || 'medium').toLowerCase()}`}>
                                      CVSS: {threat.cvss.base_score} ({threat.cvss.severity})
                                    </div>
                                  )}
                                  {threat.mitigation && (
                                    <div className="threat-modeling-mitigation">
                                      <strong>Mitigation:</strong> {threat.mitigation}
                                    </div>
                                  )}
                                </div>
                              </div>
                            ))}
                            {(currentJob.results.threat_model || currentJob.results.threats || []).length > 10 && (
                              <div className="threat-modeling-more-threats">
                                + {(currentJob.results.threat_model || currentJob.results.threats).length - 10} more threats (download full report)
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      {/* No threats message */}
                      {!currentJob.results.threat_model?.length && !currentJob.results.threats?.length && (
                        <div className="threat-modeling-no-threats">
                          <Shield size={48} color="#16a085" />
                          <p>No threats identified in this analysis, or the results are being processed.</p>
                        </div>
                      )}

                      {/* Downloads */}
                      <div className="threat-modeling-downloads">
                        <h3>Download Reports</h3>
                        <div className="threat-modeling-download-grid">
                          <button
                            className="threat-modeling-download-btn"
                            onClick={() => handleDownload(currentJob.job_id, 'json')}
                          >
                            <Download size={16} />
                            JSON Report
                          </button>
                          <button
                            className="threat-modeling-download-btn"
                            onClick={() => handleDownload(currentJob.job_id, 'markdown')}
                          >
                            <Download size={16} />
                            Markdown Report
                          </button>
                          {(currentJob.results.export_files?.sarif || exportFormats.includes('sarif')) && (
                            <button
                              className="threat-modeling-download-btn"
                              onClick={() => handleDownload(currentJob.job_id, 'sarif')}
                            >
                              <Download size={16} />
                              SARIF Export
                            </button>
                          )}
                          {(currentJob.results.export_files?.spdx || exportFormats.includes('spdx')) && (
                            <button
                              className="threat-modeling-download-btn"
                              onClick={() => handleDownload(currentJob.job_id, 'spdx')}
                            >
                              <Download size={16} />
                              SPDX Export
                            </button>
                          )}
                          {(currentJob.results.export_files?.cyclonedx || exportFormats.includes('cyclonedx')) && (
                            <button
                              className="threat-modeling-download-btn"
                              onClick={() => handleDownload(currentJob.job_id, 'cyclonedx')}
                            >
                              <Download size={16} />
                              CycloneDX Export
                            </button>
                          )}
                          {(currentJob.results.export_files?.attack_graph || enableVisualization) && (
                            <button
                              className="threat-modeling-download-btn"
                              onClick={() => handleDownload(currentJob.job_id, 'attack_graph')}
                            >
                              <Download size={16} />
                              Attack Graph
                            </button>
                          )}
                          {(currentJob.results.export_files?.risk_heatmap || enableVisualization) && (
                            <button
                              className="threat-modeling-download-btn"
                              onClick={() => handleDownload(currentJob.job_id, 'risk_heatmap')}
                            >
                              <Download size={16} />
                              Risk Heatmap
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Completed but no results */}
                  {currentJob.status === 'completed' && !currentJob.results && (
                    <div className="threat-modeling-no-results">
                      <AlertTriangle size={48} color="#f39c12" />
                      <h3>Results Not Available</h3>
                      <p>The analysis completed but the results could not be loaded. Try downloading the report directly.</p>
                      <div className="threat-modeling-download-grid" style={{ marginTop: '1rem' }}>
                        <button
                          className="threat-modeling-download-btn"
                          onClick={() => handleDownload(currentJob.job_id, 'json')}
                        >
                          <Download size={16} />
                          Download JSON
                        </button>
                        <button
                          className="threat-modeling-download-btn"
                          onClick={() => handleDownload(currentJob.job_id, 'markdown')}
                        >
                          <Download size={16} />
                          Download Markdown
                        </button>
                      </div>
                    </div>
                  )}

                  {currentJob.status === 'failed' && (
                    <div className="threat-modeling-error">
                      <div className="threat-modeling-error-icon">
                        <AlertTriangle size={48} color="#e74c3c" />
                      </div>
                      <div className="threat-modeling-error-content">
                        <h3>Analysis Failed</h3>
                        <p>{currentJob.error || 'Unknown error occurred'}</p>
                      </div>
                    </div>
                  )}

                  {(currentJob.status === 'pending' || currentJob.status === 'running') && (
                    <div className="threat-modeling-loading">
                      <div className="spinner" style={{ width: '40px', height: '40px' }}></div>
                      <p>Analysis in progress... {currentJob.progress || ''}</p>
                    </div>
                  )}
                </div>
              )}

              {/* Job History */}
              {jobs.length > 0 && (
                <div className="threat-modeling-section">
                  <div className="threat-modeling-section-header">
                    <h2>Recent Analyses</h2>
                  </div>
                  <div className="threat-modeling-history">
                    {jobs.slice(0, 10).map(job => (
                      <div key={job.job_id} className="threat-modeling-history-item">
                        <div className="threat-modeling-history-info">
                          <span className={`threat-modeling-history-status threat-modeling-status-${job.status}`}>
                            {job.status}
                          </span>
                          <span className="threat-modeling-history-type">{job.system_type}</span>
                          <span className="threat-modeling-history-date">
                            {new Date(job.created_at).toLocaleString()}
                          </span>
                          {job.threat_count > 0 && (
                            <span className="threat-modeling-history-threats">{job.threat_count} threats</span>
                          )}
                        </div>
                        <div className="threat-modeling-history-actions">
                          <button
                            className="threat-modeling-action-btn"
                            onClick={async () => {
                              const jobData = await api.getJobStatus(job.job_id);
                              setCurrentJob(jobData);
                            }}
                          >
                            <Eye size={16} />
                            View
                          </button>
                          <button
                            className="threat-modeling-action-btn threat-modeling-action-delete"
                            onClick={() => handleDelete(job.job_id)}
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}


        {/* GitHub Scanning Tab Content */}
        {activeMainTab === 'github-scanning' && (
          <GitHubScanner
            api={api}
            user={user}
            onNavigateToProfile={handleNavigateToProfile}
            onScanAttempt={handleGithubOrgScanAttempt}
            onScanSuccess={handleGithubOrgScanSuccess}
            licenseInfo={licenseInfo}
            isUnlimitedUser={isUnlimitedUser}
          />
        )}

        {/* Azure DevOps Scanning Tab Content */}
        {activeMainTab === 'azure-devops-scanning' && (
          <AzureDevOpsScanner
            api={api}
            user={user}
            onNavigateToProfile={handleNavigateToProfile}
          />
        )}

        {/* User Profile Tab Content */}
        {activeMainTab === 'profile' && (
          <div className="profile-wrapper">
            <UserProfile apiService={api} />
          </div>
        )}

        {/* Notifications Tab Content */}
        {activeMainTab === 'notifications' && (
          <NotificationCenter apiService={api} />
        )}

        {/* Dashboard Tab Content */}
        {activeMainTab === 'dashboard' && (
          <ScanResultsDashboard apiService={api} />
        )}

        {/* CI/CD Reports Tab Content */}
        {activeMainTab === 'cicd-reports' && (
          <CICDReports api={api} />
        )}

        {/* Documentation Tab Content */}
        {activeMainTab === 'documentation' && (
          <Documentation />
        )}

        {/* Organization Settings Tab Content (for tenant admins) */}
        {activeMainTab === 'org-settings' && licenseInfo?.is_tenant_admin && (
          <OrganizationSettings api={api} licenseInfo={licenseInfo} />
        )}

        {/* Admin Tab Content (for super admins only) */}
        {activeMainTab === 'admin' && licenseInfo?.is_super_admin && (
          <AdminDashboard api={api} />
        )}
      </div>
    </div>
  );
};

export default ThreatModeler;
